/**
 * The RU + EN wording for every way a dynamic-block action can DECLINE one of
 * the three heavy block-child kinds (`Polyline` / `Table` / `OLE Frame`).
 *
 * Its own module for two reasons. It keeps `actions.ts` under this repo's
 * 1000-line guideline (CLAUDE.md "Контроль размера файлов"), the same reason
 * `actions.polarStretch.ts` was split out of it. And it is a LEAF — it imports
 * only the refusal types — so `actions.polarStretch.ts` (which `actions.ts`
 * imports) could build a sink of its own without an import cycle back into
 * `actions.ts`.
 *
 * The PREDICATES that decide a refusal are not here: they live in
 * `geometryPolylineTableOle.ts`, reused from `command-engine`
 * (`isTableScaleRefused`, `isTableMirrorRefused`,
 * `isAxisAlignedFramePreservingMap`) so the evaluator and the in-place edit
 * commands can never disagree about what is representable. This module owns
 * only how the answer is phrased.
 *
 * The `"table-mirror"` row is the evaluator-side twin of the audit row the
 * in-place MIRROR command already shows
 * (`apps/web`'s `audit.entities_mirrored_tables_declined` family, fed by
 * `command-engine/tableMirrorRefusal.ts`' `summarizeTableMirrorRefusal`). Both
 * sides now ANNOUNCE the same decline the one predicate makes; before it, a
 * dynamic-block Flip of a table was declined correctly and reported nowhere.
 */

import type {
  PolylineTableOleRefusalReason,
  PolylineTableOleRefusalSink,
} from "./geometry";

/**
 * Both languages in ONE string because `EvaluateBlockResult.errors` is a plain
 * `string[]` with no message-id indirection — the evaluator is a package-level
 * leaf with no `i18n` of its own — and CLAUDE.md requires every user-facing
 * refusal to be bilingual. The in-place edit commands say the same thing
 * through `apps/web`'s i18n'd audit rows
 * (`audit.entities_rotated_ole_frames_declined` and its siblings); this is the
 * evaluator-side twin of that channel.
 */
const REFUSAL_TEXT: Record<
  PolylineTableOleRefusalReason,
  { readonly en: string; readonly ru: string }
> = {
  "table-resize": {
    en: "an ACAD_TABLE cannot be resized — its pre-evaluated *T<N> graphics would desync from the table data",
    ru: "ACAD_TABLE нельзя масштабировать — предвычисленная графика *T<N> рассинхронизируется с данными таблицы",
  },
  "table-mirror": {
    en: "an ACAD_TABLE cannot be mirrored — it is placed by an insertion point and a horizontal-direction vector, with no scale and no mirror flag, so a reflection would be stored as a 180° rotation of the pre-evaluated *T<N> graphics",
    ru: "ACAD_TABLE нельзя отразить — таблица задаётся точкой вставки и вектором горизонтального направления, без масштаба и признака зеркальности, поэтому отражение сохранилось бы как поворот предвычисленной графики *T<N> на 180°",
  },
  "ole-frame-oblique": {
    en: "an OLE2FRAME stores only two axis-aligned corners and cannot hold a rotated rectangle",
    ru: "OLE2FRAME хранит только два выровненных по осям угла и не может задать повёрнутый прямоугольник",
  },
  "polyline-partial-stretch-moves-ocs-plane": {
    en: "a partial STRETCH of a POLYLINE with a non-default 210 extrusion would split its vertices across two OCS planes",
    ru: "частичное растягивание POLYLINE с нестандартным вектором выдавливания 210 разнесло бы вершины по двум плоскостям OCS",
  },
  "polyline-nonuniform-width-scale": {
    en: "a POLYLINE that carries width cannot take a non-uniform SCALE — its 40/41 width is ONE scalar per vertex and the image of a constant-width band under an anisotropic map is a band of no single width",
    ru: "POLYLINE с ненулевой шириной нельзя масштабировать неравномерно — ширина 40/41 задаётся ОДНИМ числом на вершину, а образ полосы постоянной ширины при анизотропном преобразовании не является полосой единой ширины",
  },
};

/**
 * Build the sink the `geometry.ts` dispatchers report a declined child to,
 * appending one bilingual line per refusal to this action's `errors`.
 *
 * `errors` is the evaluator's ONE existing "this action did not do what it
 * said" channel (`EvaluateBlockResult.errors`), so a refusal travels the same
 * route as an unknown-parameter or missing-runtime-value report instead of
 * vanishing into a silent no-op (CLAUDE.md ПРИОРИТЕТ #1).
 */
export function refusalSink(
  actionLabel: string,
  errors: string[],
): PolylineTableOleRefusalSink {
  return (refusal) => {
    const text = REFUSAL_TEXT[refusal.reason];
    const who = refusal.entityId
      ? `${refusal.entityType} ${refusal.entityId}`
      : refusal.entityType;
    errors.push(
      `Action "${actionLabel}" declined ${who}: ${text.en} / ` +
        `Действие "${actionLabel}" отклонило ${who}: ${text.ru}`,
    );
  };
}
